import {
  importReasonSchema,
  pendingImportDetailSchema,
  pendingImportListSchema,
  welcomeSummarySchema,
  type ImportedDraft,
  type ImportReason,
  type PendingImportDetail,
  type PendingImportItem,
  type PendingImportList,
  type WelcomeSummary,
  type YouthProfile,
} from '@growup/accounts-shared';
import {
  Autocomplete,
  Button,
  Card,
  Field,
  Input,
  Notice,
  Select,
  Table,
  type Column,
} from '@growup/ui';
import { useCallback, useEffect, useState } from 'react';

import { getJson, messageOf, send } from '../api';
import { formatDateTime } from '../format';
import { formatDay, IMPORT_REASON_LABEL, shortName } from './labels';
import { ProfileForm } from './Profile';

const PAGE = 50;

/** Name when readable, login, and whether the person already came back. */
export function PendingCell({ item }: { item: PendingImportItem }) {
  return (
    <span className="flex min-w-0 flex-col">
      <span className="truncate font-semibold" title={item.fullName ?? undefined}>
        {item.fullName ? shortName(item.fullName) : item.login}
      </span>
      <span className="truncate text-caption text-gu-ink-muted">
        {item.fullName ? `${item.login} · ` : ''}
        {item.activated
          ? 'já entrou no site novo'
          : item.hasEmail
            ? 'ainda não entrou'
            : 'sem email'}
        {item.minor ? ' · menor' : ''}
      </span>
    </span>
  );
}

function searchPending(q: string): Promise<PendingImportItem[]> {
  const params = new URLSearchParams({ q, limit: '8' });
  return getJson(`/api/members/imports/pending?${params.toString()}`, pendingImportListSchema).then(
    (list) => list.items,
  );
}

const COLUMNS: Column<PendingImportItem>[] = [
  { key: 'person', header: 'Pessoa', cell: (i) => <PendingCell item={i} />, fill: true },
  {
    key: 'reasons',
    header: 'Falta',
    cell: (i) => <span className="text-caption">{String(i.reasons.length)}</span>,
    align: 'right',
  },
];

/**
 * Imported members still waiting for their record to be completed
 * (docs/importacao 3.2), with the launch welcome email campaign on top.
 * Possible minors only reach admins; the API decides.
 */
export function ImportsView({ canManage }: { canManage: boolean }) {
  const [reason, setReason] = useState<ImportReason | ''>('');
  const [query, setQuery] = useState('');
  const [offset, setOffset] = useState(0);
  const [list, setList] = useState<PendingImportList | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [version, setVersion] = useState(0);

  useEffect(() => setOffset(0), [query, reason]);

  useEffect(() => {
    const params = new URLSearchParams({ limit: String(PAGE), offset: String(offset) });
    if (query.trim()) params.set('q', query.trim());
    if (reason) params.set('reason', reason);
    const timer = setTimeout(() => {
      getJson(`/api/members/imports/pending?${params.toString()}`, pendingImportListSchema)
        .then((rows) => {
          setError(null);
          setList(rows);
        })
        .catch((e: unknown) => setError(messageOf(e)));
    }, 250);
    return () => clearTimeout(timer);
  }, [query, reason, offset, version]);

  return (
    <div className="flex flex-col gap-4">
      <WelcomeCard version={version} />
      <div className="grid items-start gap-4 lg:grid-cols-[26rem_1fr]">
        <Card headingLevel={2} title="Fichas por completar">
          <Field label="Procurar" hint="Escolha uma sugestão para abrir a ficha.">
            <Autocomplete
              placeholder="Login ou nome"
              value={query}
              onChange={setQuery}
              search={searchPending}
              getKey={(i) => i.accountId}
              renderItem={(i) => <PendingCell item={i} />}
              onSelect={(i) => setSelectedId(i.accountId)}
            />
          </Field>
          <Field label="O que falta">
            <Select value={reason} onChange={(e) => setReason(e.target.value as ImportReason | '')}>
              <option value="">Tudo</option>
              {importReasonSchema.options.map((r) => (
                <option key={r} value={r}>
                  {IMPORT_REASON_LABEL[r]}
                  {list?.byReason[r] ? ` (${String(list.byReason[r])})` : ''}
                </option>
              ))}
            </Select>
          </Field>
          {error ? <Notice tone="danger">{error}</Notice> : null}
          <Table
            caption="Fichas por completar"
            columns={COLUMNS}
            rows={list?.items ?? []}
            rowKey={(i) => i.accountId}
            empty={list ? 'Nenhuma ficha por completar.' : 'A carregar…'}
            onRowClick={(i) => setSelectedId(i.accountId)}
          />
          <div className="flex items-center justify-between gap-2">
            <Button
              variant="secondary"
              size="sm"
              disabled={offset === 0}
              onClick={() => setOffset(Math.max(0, offset - PAGE))}
            >
              Anteriores
            </Button>
            <span className="text-caption text-gu-ink-muted">
              {list && list.items.length > 0
                ? `${String(offset + 1)}–${String(offset + list.items.length)} de ${String(list.total)}`
                : ''}
            </span>
            <Button
              variant="secondary"
              size="sm"
              disabled={!list || offset + PAGE >= list.total}
              onClick={() => setOffset(offset + PAGE)}
            >
              Seguintes
            </Button>
          </div>
        </Card>
        {selectedId ? (
          <PendingDetail
            key={`${selectedId}-${String(version)}`}
            accountId={selectedId}
            canManage={canManage}
            onChanged={(completed) => {
              if (completed) setSelectedId(null);
              setVersion((v) => v + 1);
            }}
          />
        ) : (
          <p className="text-gu-ink-muted">
            Escolha alguém da lista para ver o que veio da base antiga e completar a ficha.
          </p>
        )}
      </div>
    </div>
  );
}

/** The launch campaign at a glance; the sending itself is a command (docs/importacao/correr.md). */
function WelcomeCard({ version }: { version: number }) {
  const [summary, setSummary] = useState<WelcomeSummary | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    getJson('/api/members/imports/welcome/summary', welcomeSummarySchema)
      .then(setSummary)
      .catch((e: unknown) => setError(messageOf(e)));
  }, [version]);

  if (error) return <Notice tone="danger">{error}</Notice>;
  if (!summary) return null;
  return (
    <Card headingLevel={2} title="Emails de boas-vindas">
      <dl className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6">
        <Stat label="Importados" value={summary.imported} />
        <Stat label="Email enviado" value={summary.sent} />
        <Stat label="Já entraram" value={summary.activated} />
        <Stat label="Por enviar" value={summary.toSend} />
        <Stat label="Sem email" value={summary.noEmail} />
        <Stat label="Desativados" value={summary.disabled} />
      </dl>
      <p className="text-caption text-gu-ink-muted">
        No lançamento, o email com o link para escolher a password sai em lotes por um comando no
        servidor; quem já o recebeu não o volta a receber. Os desativados no sistema antigo só o
        recebem se o reenviar na ficha.
      </p>
    </Card>
  );
}

function toProfile(draft: ImportedDraft): YouthProfile {
  return {
    fullName: draft.fullName ?? '',
    birthDate: draft.birthDate ?? '',
    contactEmail: draft.contactEmail,
    phone: draft.phone,
    ccNumber: draft.ccNumber ?? '',
    address: {
      street: draft.address.street ?? '',
      postalCode: draft.address.postalCode ?? '',
      locality: draft.address.locality ?? '',
      country: draft.address.country ?? 'PT',
    },
  };
}

/** What came from the old database, what is missing, and the actions. */
function PendingDetail({
  accountId,
  canManage,
  onChanged,
}: {
  accountId: string;
  canManage: boolean;
  onChanged: (completed: boolean) => void;
}) {
  const [detail, setDetail] = useState<PendingImportDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);
  const [completing, setCompleting] = useState(false);
  const [guardianEmail, setGuardianEmail] = useState('');

  const load = useCallback(() => {
    getJson(`/api/members/imports/pending/${accountId}`, pendingImportDetailSchema)
      .then((d) => {
        setError(null);
        setDetail(d);
      })
      .catch((e: unknown) => setError(messageOf(e)));
  }, [accountId]);

  useEffect(load, [load]);

  if (!detail) return error ? <Notice tone="danger">{error}</Notice> : null;
  const { draft } = detail;
  const missing = (value: string | null) => value ?? '— falta';
  const rows: [string, string][] = [
    ['Nome', missing(draft.fullName)],
    [
      'Data de nascimento',
      draft.birthDate
        ? `${formatDay(draft.birthDate)}${draft.birthDateUnconfirmed ? ' (ano a confirmar)' : ''}`
        : '— falta',
    ],
    ['Email de contacto', missing(draft.contactEmail)],
    ['Telemóvel', missing(draft.phone)],
    ['Cartão de Cidadão', missing(draft.ccNumber)],
    [
      'Morada',
      [
        draft.address.street,
        draft.address.postalCode,
        draft.address.locality,
        draft.address.country,
      ]
        .map((v) => v ?? '?')
        .join(', '),
    ],
  ];
  const canResend = canManage && detail.kind === 'standard' && detail.hasEmail && !detail.activated;

  return (
    <Card
      headingLevel={2}
      ruled
      eyebrow={`Login ${detail.login}`}
      title={draft.fullName ?? detail.login}
    >
      <div className="flex flex-wrap gap-x-3 gap-y-1 text-gu-ink-muted">
        <span>Importado a {formatDateTime(detail.importedAt)}</span>
        {detail.minor ? <span>· Menor de idade (ou sem data)</span> : null}
        <span>
          ·{' '}
          {detail.activated
            ? 'Já entrou no site novo'
            : detail.welcomeSentAt
              ? `Email de boas-vindas a ${formatDateTime(detail.welcomeSentAt)}`
              : 'Sem email de boas-vindas'}
        </span>
      </div>
      {error ? <Notice tone="danger">{error}</Notice> : null}
      {done ? <Notice tone="success">{done}</Notice> : null}
      <div>
        <p className="font-sub text-label font-bold uppercase text-gu-ink-muted">Falta</p>
        <ul className="list-inside list-disc">
          {detail.reasons.map((r) => (
            <li key={r}>
              {importReasonSchema.safeParse(r).success ? IMPORT_REASON_LABEL[r as ImportReason] : r}
            </li>
          ))}
        </ul>
      </div>
      {completing ? (
        <ProfileForm
          accountId={accountId}
          profile={toProfile(draft)}
          submitLabel="Completar e ativar"
          submit={(profile) =>
            send('POST', `/api/members/imports/pending/${accountId}/complete`, {
              ...profile,
              guardianEmail: guardianEmail.trim() === '' ? null : guardianEmail.trim(),
            }).then(() => undefined)
          }
          extra={
            <Field
              label="Email do encarregado de educação"
              hint="Só para menores: segue o convite para autorizar"
              optional
            >
              <Input
                type="email"
                value={guardianEmail}
                onChange={(e) => setGuardianEmail(e.target.value)}
              />
            </Field>
          }
          onDone={(saved) => {
            setCompleting(false);
            if (saved) onChanged(true);
          }}
        />
      ) : (
        <>
          <dl className="grid gap-x-6 gap-y-2 text-body sm:grid-cols-[11rem_1fr]">
            {rows.map(([label, value]) => (
              <div key={label} className="contents">
                <dt className="font-sub text-label font-bold uppercase text-gu-ink-muted">
                  {label}
                </dt>
                <dd className="break-words">{value}</dd>
              </div>
            ))}
          </dl>
          {canManage ? (
            <div className="flex flex-wrap gap-3">
              <Button size="sm" onClick={() => setCompleting(true)}>
                Completar ficha
              </Button>
              {canResend ? (
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => {
                    setError(null);
                    setDone(null);
                    send('POST', `/api/members/imports/${accountId}/welcome`)
                      .then(() => {
                        setDone('Email de boas-vindas enviado.');
                        onChanged(false);
                      })
                      .catch((e: unknown) => setError(messageOf(e)));
                  }}
                >
                  {detail.welcomeSentAt
                    ? 'Reenviar email de boas-vindas'
                    : 'Enviar email de boas-vindas'}
                </Button>
              ) : null}
            </div>
          ) : null}
        </>
      )}
    </Card>
  );
}

function Stat({ label, value }: { label: string; value: number }) {
  return (
    <div className="flex flex-col gap-1 border-l-2 border-gu-accent pl-3">
      <dt className="text-caption text-gu-ink-muted">{label}</dt>
      <dd className="font-display text-title">{value}</dd>
    </div>
  );
}
